import React, { useMemo } from 'react';
import { Line } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Tooltip,
  Filler,
} from 'chart.js';

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Tooltip, Filler);

type SeriesPoint = { year: number; doy: number; value: number };

export interface MiniSparklineProps {
  title?: string;
  unit?: string;
  // Either provide a single series or multiple labelled series
  series?: SeriesPoint[];
  seriesList?: Array<{ label: string; color?: string; series: SeriesPoint[] }>;
  width?: number;
  height?: number;
  color?: string;
  maxPoints?: number; // last N points
  showHeader?: boolean;
  showAxes?: boolean;
  activeIndex?: number; // highlight a cursor at this index
  clipToActive?: boolean; // show only up to active index
  labelFormatter?: (d: SeriesPoint, idx: number, total: number) => string;
  beginAtZero?: boolean; // force y-axis to start at zero
}

const MiniSparkline: React.FC<MiniSparklineProps> = ({
  title = 'Soil Moisture',
  unit = '',
  series,
  seriesList,
  width = 140,
  height = 56,
  color = '#941C30',
  maxPoints = 30,
  showHeader = false,
  showAxes = false,
  activeIndex,
  clipToActive = false,
  labelFormatter,
  beginAtZero = false,
}) => {
  const slicedSingle = useMemo(() => {
    if (!series) return [] as SeriesPoint[];
    const clean = series.filter((d) => typeof d.value === 'number');
    const trimmed = clean.slice(Math.max(0, clean.length - maxPoints));
    if (clipToActive && typeof activeIndex === 'number') {
      const idx = Math.min(Math.max(0, activeIndex), trimmed.length - 1);
      return trimmed.slice(0, idx + 1);
    }
    return trimmed;
  }, [series, maxPoints, clipToActive, activeIndex]);

  const aligned = useMemo(() => {
    if (!seriesList || seriesList.length === 0) return null as null | {
      labels: string[];
      datasets: Array<{ label: string; color?: string; values: number[] }>;
    };
    // Use DOY within same year; assume same year across entries
    const build = (sp: SeriesPoint[]) => sp.filter(d => typeof d.value === 'number');
    const allSeries = seriesList.map(s => ({ label: s.label, color: s.color, s: build(s.series) }));
    // Labels based on the longest series
    const longest = allSeries.reduce((a, b) => (a.s.length >= b.s.length ? a : b));
    const clamp = (arr: SeriesPoint[]) => arr.slice(Math.max(0, arr.length - maxPoints));
    const base = clamp(longest.s);
    const cutIndex = (clipToActive && typeof activeIndex === 'number')
      ? Math.min(Math.max(0, activeIndex), base.length - 1)
      : null;
    const valuesBySeries = allSeries.map(({ label, color, s }) => ({
      label,
      color,
      values: (cutIndex !== null ? clamp(s).slice(0, cutIndex + 1) : clamp(s)).map(d => d.value),
    }));
    const labelsClampedArr = cutIndex !== null ? base.slice(0, cutIndex + 1) : base;
    const labelsClamped = labelsClampedArr.map(d => `${d.year}-D${d.doy}`);
    return { labels: labelsClamped, datasets: valuesBySeries };
  }, [seriesList, maxPoints, clipToActive, activeIndex]);

  const labels = useMemo(() => {
    if (aligned) return aligned.labels;
    return slicedSingle.map((d, i, arr) => labelFormatter ? labelFormatter(d, i, arr.length) : `${d.year}-D${d.doy}`);
  }, [aligned, slicedSingle, labelFormatter]);

  const data = useMemo(() => {
    if (aligned) {
      const labelsFinal = aligned.labels;
      return {
        labels: labelsFinal,
        datasets: aligned.datasets.map((ds, idx) => ({
          label: ds.label,
          data: ds.values,
          borderColor: ds.color || `hsl(${(idx * 120) % 360}, 70%, 45%)`,
          borderWidth: 1.4,
          pointRadius: 0,
          pointHoverRadius: 2.5,
          fill: false,
          tension: 0.25,
        })),
      };
    }
    return {
      labels,
      datasets: [
        {
          data: slicedSingle.map((d) => d.value),
          fill: true,
          borderColor: color,
          borderWidth: 1.5,
          backgroundColor: (ctx: any) => {
            const { chart } = ctx;
            const { ctx: c, chartArea } = chart;
            if (!chartArea) return 'rgba(148,28,48,0.12)';
            const gradient = c.createLinearGradient(0, chartArea.top, 0, chartArea.bottom);
            gradient.addColorStop(0, 'rgba(148,28,48,0.20)');
            gradient.addColorStop(1, 'rgba(148,28,48,0.02)');
            return gradient;
          },
          tension: 0.25,
          pointRadius: 0,
          pointHoverRadius: 3,
        },
      ],
    };
  }, [aligned, labels, slicedSingle, color]);

  return (
    <div style={{ width, height, display: 'flex', flexDirection: 'column' }}>
      {showHeader && (
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div style={{ fontSize: 12, fontWeight: 700, color: '#1E0C0D' }}>{title}</div>
          <div style={{ fontSize: 11, color: '#941C30' }}>{unit}</div>
        </div>
      )}
      <div style={{ flex: 1, minHeight: 0 }}>
        <Line
          data={data}
          height={showHeader ? height - 24 : height - 2}
          options={{
            responsive: true,
            maintainAspectRatio: false,
            animation: {
              duration: 700,
              easing: 'easeOutQuart',
            },
            plugins: {
              legend: { display: showAxes && !!seriesList },
              tooltip: {
                enabled: true,
                intersect: false,
                mode: 'index',
                backgroundColor: 'rgba(255,255,255,0.95)',
                titleColor: '#941C30',
                bodyColor: '#1E0C0D',
                borderColor: '#941C30',
                borderWidth: 1,
                padding: 8,
                displayColors: false,
                callbacks: {
                  title(items) {
                    const i = items[0]?.dataIndex ?? 0;
                    const d = slicedSingle[i];
                    return d ? `${d.year} (DOY ${d.doy})` : '';
                  },
                  label(item) {
                    return ` ${item.formattedValue}${unit ? ' ' + unit : ''}`;
                  },
                },
              },
            },
            interaction: { mode: 'index', intersect: false },
            scales: {
              x: {
                display: showAxes,
                ticks: { maxTicksLimit: 8 },
                grid: { display: showAxes, color: 'rgba(0,0,0,0.06)' },
              },
              y: {
                display: showAxes,
                title: showAxes && unit ? { display: true, text: unit } : undefined,
                grid: { display: showAxes, color: 'rgba(0,0,0,0.06)' },
                beginAtZero,
              },
            },
            elements: {
              line: { capBezierPoints: true },
            },
          }}
          plugins={[
            {
              id: 'activeIndexLine',
              afterDraw: (chart: any) => {
                if (typeof activeIndex !== 'number') return;
                const count = chart.data?.labels?.length ?? 0;
                if (count === 0) return;
                const idx = Math.min(Math.max(0, activeIndex), count - 1);
                const meta = chart.getDatasetMeta(0);
                const el = meta?.data?.[idx];
                if (!el) return;
                const ctx = chart.ctx;
                ctx.save();
                ctx.beginPath();
                ctx.moveTo(el.x, chart.chartArea.top);
                ctx.lineTo(el.x, chart.chartArea.bottom);
                ctx.lineWidth = 1.5;
                ctx.setLineDash([4, 4]);
                ctx.strokeStyle = 'rgba(148,28,48,0.5)';
                ctx.stroke();
                ctx.restore();
              },
            },
          ]}
        />
      </div>
    </div>
  );
};

export default MiniSparkline;
